import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'

import { BookingType, BookingTypeBuilding } from './booking-types.types'

export const USE_GET_BOOKING_TYPES_QUERY_KEY = 'useGetBookingTypes'
export const USE_GET_BOOKING_TYPE_QUERY_KEY = 'useGetBookingType'

// Note: BE returns inactive (soft-deleted) types too and does not populate buildings
export const getBookingTypes = async (signal?: AbortSignal) => {
  const response = await authAPI.get<BookingType[]>('/booking-type/all', { signal })
  return response.data.filter(({ active }) => active)
}

export const getBookingType = async (id: number, signal?: AbortSignal) => {
  const response = await authAPI.get<BookingType>(`/booking-type/one/${id}`, { signal })
  return response.data
}

export const useGetBookingTypes = () => {
  return useQuery({
    queryFn: ({ signal }) => getBookingTypes(signal),
    queryKey: [USE_GET_BOOKING_TYPES_QUERY_KEY],
  })
}

export const useGetBookingType = (id: number | undefined) => {
  return useQuery({
    enabled: id !== undefined,
    queryFn: ({ signal }) => getBookingType(id as number, signal),
    queryKey: [USE_GET_BOOKING_TYPE_QUERY_KEY, id],
  })
}

const NO_BUILDINGS: BookingTypeBuilding[] = []

// Note: the list endpoint does not populate buildings, so they come from the detail endpoint of each
// listed type (same query key as the edit popup, so the two share one cache entry)
export const useGetBookingTypesBuildings = (ids: number[]) => {
  const results = useQueries({
    queries: ids.map((id) => ({
      queryFn: ({ signal }: { signal: AbortSignal }) => getBookingType(id, signal),
      queryKey: [USE_GET_BOOKING_TYPE_QUERY_KEY, id],
      select: (type: BookingType) => type.buildings ?? NO_BUILDINGS,
    })),
  })

  // Note: undefined while a type's details are loading; a failed request counts as no buildings
  return new Map(
    ids.map((id, index) => [
      id,
      results[index]?.data ?? (results[index]?.isError ? NO_BUILDINGS : undefined),
    ]),
  )
}

export const useInvalidateBookingTypes = () => {
  const queryClient = useQueryClient()
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: [USE_GET_BOOKING_TYPES_QUERY_KEY] }),
      queryClient.invalidateQueries({ queryKey: [USE_GET_BOOKING_TYPE_QUERY_KEY] }),
    ])
}
